import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon } from '@modulor/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '../Input/Input';
import { getRootRoute } from '../../utils/routes';
import { getDataTestAttrs } from '../../utils/testing-locators';
import { NAMESPACES } from '../../constants/namespaces';
import {
  SEARCH_FORM_ICON,
  SEARCH_FORM_INPUT,
  SEARCH_FORM_CLEAR,
  SEARCH_FORM_BACK,
} from '../../constants/dataTestIds';
import styles from './SearchInput.module.css';

const SearchInput = forwardRef(
  ({ value, onChange, onClear, withBackButton, className, ...props }, ref) => {
    const { t, ready } = useTranslation(NAMESPACES.INITIAL, {
      useSuspense: false,
    });
    const placeholder = ready ? t('Start typing the name of the game') : '';

    return (
      <label className={classNames(styles.component, className)}>
        {withBackButton ? (
          <Link
            to={getRootRoute()}
            onClick={onClear}
            className={styles['back-link']}
            {...getDataTestAttrs(SEARCH_FORM_BACK)}
          >
            <Icon
              name="chevron_left"
              className={styles['arrow-back-icon']}
              size={16}
            />
          </Link>
        ) : (
          <Icon
            name="search"
            className={styles['search-icon']}
            {...getDataTestAttrs(SEARCH_FORM_ICON)}
          />
        )}

        <Input
          ref={ref}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange && onChange(e.target.value)}
          className={styles.field}
          {...getDataTestAttrs(SEARCH_FORM_INPUT)}
          {...props}
        />

        <button
          type="button"
          className={classNames(
            styles['clear-button'],
            onClear && value && styles.visible
          )}
          onClick={onClear}
          {...getDataTestAttrs(SEARCH_FORM_CLEAR)}
        >
          <Icon name="close" />
        </button>
      </label>
    );
  }
);

SearchInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  withBackButton: PropTypes.bool,
};

SearchInput.defaultProps = {
  value: '',
  withBackButton: false,
};

export default SearchInput;
